import { useState } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { interpolateTemplate } from "lib/i18n/dictionary";

import { assistantName } from "./site-assistant";
import { DisconnectAssistantDialog } from "./site-assistant-card";
import { SiteHeader } from "./site-header";
import { SiteLayout } from "./site-layout";
import { SITE_ROUTE } from "./site.routes";
import { OwnAssistantLockedCard } from "./site-assistant-card";
import { useSiteEntitlements } from "./site.hooks";
import { useOpenClaude, useSiteAssistantConnection } from "./use-site-assistant-connection";
import { useSiteTranslations } from "./use-site-translations";

const STEPS = ["connect", "create", "publish"] as const;

function CreateWithClaude() {
  const { t } = useSiteTranslations();
  const { connection, onConnect, isCreating, onDisconnect, isDisconnecting } =
    useSiteAssistantConnection();
  const openClaude = useOpenClaude();
  const [confirming, setConfirming] = useState(false);
  const client = assistantName(connection?.clientName);
  const currentStep = connection ? 1 : 0;

  return (
    <Grid container spacing={3} data-testid="provider-site-create-with-claude-screen">
      <Grid item xs={12}>
        <SiteHeader
          title={t.assistant.create.title}
          description={t.assistant.create.body}
          backTo={SITE_ROUTE}
        />
      </Grid>
      <Grid item xs={12} lg={7}>
        <Card>
          <SoftBox p={3}>
            {STEPS.map((step, index) => (
              <SoftBox key={step}>
                {index > 0 ? <Divider sx={{ my: 0 }} /> : null}
                <SoftBox display="flex" alignItems="center" gap={2} py={1.5}>
                  <SoftBox
                    width="1.75rem"
                    height="1.75rem"
                    flexShrink={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="50%"
                    variant={index === currentStep ? "gradient" : undefined}
                    bgColor={index <= currentStep ? "info" : "grey-200"}
                    opacity={index < currentStep ? 0.5 : 1}
                  >
                    <SoftTypography
                      variant="caption"
                      fontWeight="bold"
                      color={index <= currentStep ? "white" : "text"}
                    >
                      {index + 1}
                    </SoftTypography>
                  </SoftBox>
                  <SoftTypography variant="button" fontWeight="regular">
                    {t.assistant.create.steps[step]}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            ))}
          </SoftBox>
        </Card>
      </Grid>
      <Grid item xs={12} lg={5}>
        <SoftBox display="flex" flexDirection="column" gap={3}>
          {connection ? (
            <Card data-testid="provider-site-assistant-connected">
              <SoftBox p={3} display="flex" alignItems="center" gap={2}>
                <SoftBox width="0.625rem" height="0.625rem" borderRadius="50%" bgColor="success" />
                <SoftTypography variant="button" fontWeight="medium" sx={{ flexGrow: 1 }}>
                  {interpolateTemplate(t.assistant.connected, { client })}
                </SoftTypography>
                <SoftButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled={isDisconnecting}
                  onClick={() => setConfirming(true)}
                  data-testid="provider-site-assistant-disconnect"
                >
                  {t.assistant.disconnect}
                </SoftButton>
              </SoftBox>
            </Card>
          ) : null}
          <Card>
            <SoftBox p={3} display="flex" flexDirection="column" gap={1.5}>
              {connection ? (
                <SoftButton
                  variant="gradient"
                  color="info"
                  size="large"
                  onClick={() => openClaude(null)}
                  data-testid="provider-site-create-with-claude"
                >
                  <Icon sx={{ mr: 1 }}>auto_awesome</Icon>
                  {t.assistant.createWithClaude}
                </SoftButton>
              ) : (
                <SoftButton
                  variant="gradient"
                  color="info"
                  size="large"
                  disabled={isCreating}
                  onClick={() => void onConnect()}
                  data-testid="provider-site-assistant-connect"
                >
                  {isCreating ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    t.assistant.connect
                  )}
                </SoftButton>
              )}
              <SoftTypography variant="caption" color="text" textAlign="center">
                {t.assistant.create.note}
              </SoftTypography>
            </SoftBox>
          </Card>
        </SoftBox>
      </Grid>
      <DisconnectAssistantDialog
        client={client}
        open={confirming}
        busy={isDisconnecting}
        onConfirm={() => void onDisconnect(client).finally(() => setConfirming(false))}
        onCancel={() => setConfirming(false)}
      />
    </Grid>
  );
}

// No site and no AI designer: the three steps to create the site in the Provider's own Claude.
export function SiteClaudePage() {
  return <SiteLayout>{() => <CreateWithClaudeIfIncluded />}</SiteLayout>;
}

// The steps page opened by a link or bookmark on a Plan without the own assistant shows why not.
function CreateWithClaudeIfIncluded() {
  const { isLoading, ownAssistant } = useSiteEntitlements();
  return !isLoading && !ownAssistant ? <OwnAssistantLockedCard /> : <CreateWithClaude />;
}
